import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { agentService, CallLeadInfo } from '../services/agentService';
import InCallPopup, { CallDispositionMode, DispositionValue } from '../components/Agent/InCallPopup';
import ScriptAssistPopup from '../components/Agent/ScriptAssistPopup';
import { PhoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import {
  isCallStillUp,
  mayPlaceParkLeg,
  parkLegReportId,
  parkOccupyingCallId,
  shouldQueueSupersededWrapUp,
  shouldRejectIncomingCall,
  wrapUpMayClaimBrowser,
  wrapUpKey,
} from './inboundCallGuard';
import {
  createRemoteAudioMemo,
  foldInboundAudio,
  readRemoteAudio,
  sampleInboundAudio,
  summariseCallAudio,
  RemoteAudioMemo,
  RemoteAudioReading,
} from './remoteAudioMonitor';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AgentCallStatus =
  | 'loading'
  | 'connecting'
  | 'ready'
  | 'ringing'
  | 'in-call'
  | 'error';

export interface OutboundCallParams {
  destinationNumber: string;
  callerNumber: string;
  callAttemptId: string;
  routingAttemptId: string;
  routingLegId: string | null;
  campaignId: string;
  leadId: string;
  leadName: string | null;
  leadPhone: string | null;
  leadEmail: string | null;
  leadNotes?: string | null;
  companyName: string | null;
  campaignName: string | null;
  scriptName?: string | null;
  scriptContent?: string | null;
  scriptStructure?: Record<string, any> | null;
  agentId: string;
  leadStatus?: string | null;
  preDialStatus?: string | null;
  subStageId?: string | null;
  dispositionMode?: CallDispositionMode;
}

interface WebRTCContextType {
  /** Current WebRTC connection / call status */
  status: AgentCallStatus;
  /** Error message if status is 'error' */
  error: string | null;
  /** Whether the current call is muted */
  isMuted: boolean;
  /** Reconnect the WebRTC client */
  reconnect: () => void;
  /** Hang up the active call */
  hangUp: () => void;
  /** Toggle mute on the active call */
  toggleMute: () => void;
  /** Submit disposition for the current call */
  handleDisposition: (value: DispositionValue, notes?: string) => Promise<void>;
  /** Lead info for the current / most recent call */
  callLeadInfo: CallLeadInfo | null;
  /** Call duration in seconds */
  seconds: number;
  /** Whether a disposition has been submitted */
  dispositionSubmitted: DispositionValue | null;
  /** Register a callback to run after disposition completes (e.g. navigation) */
  setOnDispositionComplete: (cb: ((payload?: { leadId?: string | null; disposition?: DispositionValue | null }) => void) | null) => void;
  /** Whether voicemail was detected by AMD on the current call */
  voicemailDetected: boolean;
  /**
   * Whether audio is actually arriving on the current call, and whether it
   * carries any sound. Null off-call. See `remoteAudioMonitor`.
   */
  remoteAudio: RemoteAudioReading | null;
  /** Initiate an outbound call directly from the browser via WebRTC */
  makeOutboundCall: (params: OutboundCallParams) => void;
  /** Whether the active call is an admin test call */
  isTestCall: boolean;
  /** Initiate a direct test call (admin only, no campaign routing) */
  startTestCall: (fromNumber: string, toNumber: string) => void;
  /** Hang up an active admin test call */
  hangUpTestCall: () => void;
  /**
   * Unlock audio playback. Must be called from a user gesture (e.g. the
   * availability toggle click) so the browser's autoplay policy allows the
   * remote-audio element to play() when a call later auto-answers.
   */
  unlockAudio: () => void;
  /**
   * The parked leg this browser holds, reported to the server on each heartbeat.
   *
   * A getter, not a value: the leg is tracked in a ref that nothing re-renders
   * on, so a value snapshotted at render time is null for the life of the
   * context object and the server never learns which leg the browser holds.
   */
  getParkCallId: () => string | null;
  /** Act on the server's parking directive from a heartbeat response. */
  applyParkDirective: (result: {
    shouldPark: boolean;
    dropLeg?: boolean;
    park?: { destinationNumber: string; callerNumber: string; agentId: string };
  }) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WebRTCContext = createContext<WebRTCContextType | null>(null);

export function useWebRTC(): WebRTCContextType {
  const ctx = useContext(WebRTCContext);
  if (!ctx) {
    throw new Error('useWebRTC must be used inside <WebRTCProvider>');
  }
  return ctx;
}

export function useOptionalWebRTC(): WebRTCContextType | null {
  return useContext(WebRTCContext);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * Let go of the customer's audio when a call ends.
 *
 * With a parked leg this cannot simply empty the audio element. The parked leg
 * is the agent's only audio path and it outlives every call on it, but nothing
 * ever re-attaches it: unholding happens on the server, and the browser is
 * never told. Emptying the element here would leave the agent hearing silence
 * on the next customer while their leg looked perfectly healthy to everyone
 * else — the exact failure park is supposed to remove, moved into the browser.
 *
 * So the parked stream goes back on instead of null, and only a browser with no
 * parked leg ends up with an empty element.
 */
function releaseRemoteAudio(
  audio: HTMLAudioElement | null,
  parkCall: { remoteStream?: MediaStream | null } | null
): void {
  if (!audio) return;

  const parkStream = parkCall?.remoteStream;
  if (parkStream && parkStream.getAudioTracks && parkStream.getAudioTracks().length > 0) {
    if (audio.srcObject !== parkStream) {
      parkStream.getAudioTracks().forEach((t) => { t.enabled = true; });
      audio.srcObject = parkStream;
      audio.volume = 1.0;
      audio.muted = false;
      audio.play().catch(() => {});
    }
    return;
  }

  audio.srcObject = null;
}

export const WebRTCProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // ─── WebRTC State ──────────────────────────────────────────────────────
  const clientRef = useRef<any>(null);
  const activeCallRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const [status, setStatus] = useState<AgentCallStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  // ─── Call / Popup State ────────────────────────────────────────────────
  const isOutboundRef = useRef(false); // true when agent initiated the call via makeOutboundCall
  /** Call IDs created via client.newCall — distinguishes outbound from server transfers. */
  const outboundCallIdsRef = useRef<Set<string>>(new Set());
  /** Prevent double-answer and ignore destroy events from superseded calls. */
  const answeredCallIdsRef = useRef<Set<string>>(new Set());
  const isTestCallRef = useRef(false); // true when admin initiated a test call
  const [isTestCall, setIsTestCall] = useState(false);
  const [callLeadInfo, setCallLeadInfo] = useState<CallLeadInfo | null>(null);
  const callLeadInfoRef = useRef<CallLeadInfo | null>(null);
  const [ringingLeadInfo, setRingingLeadInfo] = useState<CallLeadInfo | null>(null);
  const ringingLeadInfoRef = useRef<CallLeadInfo | null>(null);
  const ringingLeadFetchedForRef = useRef<string | null>(null);
  const [dispositionSubmitted, setDispositionSubmitted] = useState<DispositionValue | null>(null);
  const dispositionSubmittedRef = useRef<DispositionValue | null>(null);
  const dispositionModeRef = useRef<CallDispositionMode>('full');
  const [dispositionMode, setDispositionMode] = useState<CallDispositionMode>('full');
  const [showPopup, setShowPopup] = useState(false);
  const [awaitingDisposition, setAwaitingDisposition] = useState(false);
  const awaitingDispositionRef = useRef(false);
  /** Locked lead/call context for disposition — survives new-call races. */
  const dispositionLeadInfoRef = useRef<CallLeadInfo | null>(null);
  /** Previous calls parked when a live-connect arrives during wrap-up. */
  const pendingDispositionQueueRef = useRef<CallLeadInfo[]>([]);
  const [pendingDispositionCount, setPendingDispositionCount] = useState(0);
  const liveConnectCheckInFlightRef = useRef<Set<string>>(new Set());
  /**
   * Calls whose wrap-up has already been opened, so the two signals that can
   * open it — this browser's own `hangup` and the server's call-ended alert —
   * cannot open it twice. Keyed by call attempt, which is unique per call.
   */
  const wrapUpStartedForRef = useRef<Set<string>>(new Set());
  /** Lead info keyed by WebRTC call.id so a superseded hangup can still wrap up. */
  const leadInfoByCallIdRef = useRef<Map<string, CallLeadInfo>>(new Map());
  /**
   * The agent's parked leg — a call that stays up across customer calls so one
   * can be dropped into it without ringing anything. Kept strictly apart from
   * activeCallRef: seat claiming, auto-answer and audio teardown all key off
   * that, and a parked leg must not look like a call in progress.
   */
  const parkCallRef = useRef<any>(null);
  const parkCallIdRef = useRef<string | null>(null);
  /** Guards against a second park while the first is still coming up. */
  const parkingInFlightRef = useRef(false);
  /**
   * The call a screening notice has already been shown for.
   *
   * Keyed rather than a boolean that something has to remember to clear: AMD
   * restarts on a screened call so the event repeats, and a flag reset on
   * wrap-up stays stuck for any call that never opens one — silently
   * suppressing the notice on the next call.
   */
  const screeningNotifiedForRef = useRef<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onDispositionCompleteRef = useRef<((payload?: { leadId?: string | null; disposition?: DispositionValue | null }) => void) | null>(null);
  const hangUpRef = useRef<(() => Promise<void>) | null>(null);
  const [voicemailDetected, setVoicemailDetected] = useState(false);
  /** Inbound audio health for the live call. Null off-call. */
  const [remoteAudio, setRemoteAudio] = useState<RemoteAudioReading | null>(null);
  /**
   * The running audio picture, kept where the hangup handler can reach it.
   *
   * The monitor's memo lives inside its effect, and that effect is torn down by
   * the same status change the call's ending causes — so by the time we know a
   * call is over, the evidence about it is already gone. This is the copy that
   * outlives it, tagged with the call it belongs to so a stale one is never
   * filed against the next call.
   */
  const audioMemoRef = useRef<{ callId: string; memo: RemoteAudioMemo } | null>(null);
  const statusRef = useRef<AgentCallStatus>('loading');
  // True when WE flipped the agent to unavailable because the socket dropped.
  // On reconnect we restore availability; a manual "unavailable" toggle by the
  // agent is never overridden because this flag is only set after confirming
  // the agent was available at the moment of the drop.
  const availabilityDroppedBySocketRef = useRef(false);
  const outboundRingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  /**
   * Watch the inbound audio for as long as a call is up.
   *
   * Driven off `status` rather than hooked into the twenty places that set or
   * clear `activeCallRef` — one timer that reads the ref each tick cannot fall
   * out of step with them, and it survives a call being replaced under it: a
   * new call id restarts the window so the previous call's silence is not
   * carried over. Everything it touches is read-only, and a failed poll
   * contributes no evidence rather than a false alarm.
   */
  useEffect(() => {
    if (status !== 'in-call') {
      setRemoteAudio(null);
      return;
    }

    let stopped = false;
    let watchedCallId: string | null = activeCallRef.current?.id ?? null;
    let memo = createRemoteAudioMemo(Date.now());

    const tick = async () => {
      const call = activeCallRef.current;
      const callId = call?.id ?? null;
      if (callId !== watchedCallId) {
        watchedCallId = callId;
        memo = createRemoteAudioMemo(Date.now());
      }
      const sample = await sampleInboundAudio(call?.peer?.instance ?? null);
      if (stopped) return;
      memo = foldInboundAudio(memo, sample);
      if (callId) audioMemoRef.current = { callId, memo };
      const reading = readRemoteAudio(memo, sample.at);
      setRemoteAudio((prev) =>
        prev &&
        prev.state === reading.state &&
        Math.round(prev.level * 50) === Math.round(reading.level * 50) &&
        Math.floor(prev.quietForMs / 1000) === Math.floor(reading.quietForMs / 1000)
          ? prev
          : reading
      );
    };

    void tick();
    const interval = setInterval(() => { void tick(); }, 500);
    return () => {
      stopped = true;
      clearInterval(interval);
      setRemoteAudio(null);
    };
  }, [status]);

  useEffect(() => {
    awaitingDispositionRef.current = awaitingDisposition;
  }, [awaitingDisposition]);

  const lockDispositionContext = useCallback((info: CallLeadInfo | null | undefined) => {
    if (!info || (!info.leadId && !info.callAttemptId)) return;
    const snapshot: CallLeadInfo = { ...info };
    dispositionLeadInfoRef.current = snapshot;
    setCallLeadInfo(snapshot);
    void agentService.setDispositionPending(true).catch((err) => {
      console.warn('[WebRTC] setDispositionPending(true) failed:', err?.message || err);
    });
  }, []);

  const releaseDispositionContext = useCallback(() => {
    dispositionLeadInfoRef.current = null;
    void agentService.setDispositionPending(false).catch((err) => {
      console.warn('[WebRTC] setDispositionPending(false) failed:', err?.message || err);
    });
  }, []);

  const enterAwaitingDisposition = useCallback(() => {
    const info = dispositionLeadInfoRef.current ?? callLeadInfoRef.current;
    if (!info?.leadId && !info?.callAttemptId) return;
    setAwaitingDisposition(true);
    setShowPopup(true);
    lockDispositionContext(info);
  }, [lockDispositionContext]);

  /**
   * Open wrap-up once per call, whichever signal gets here first.
   *
   * Today the browser's `hangup` almost always wins, because the customer
   * hanging up tears down the agent's leg too. Running both now — rather than
   * switching over — is what proves this guard before an agent's leg starts
   * outliving calls and the server signal becomes the only one.
   *
   * Returns whether this call was the one that opened wrap-up.
   */
  const beginWrapUp = useCallback((reason: string): boolean => {
    const info = dispositionLeadInfoRef.current ?? callLeadInfoRef.current;
    const key = info?.callAttemptId || info?.leadId;
    if (!key) return false;

    if (wrapUpStartedForRef.current.has(key)) {
      console.log(`[WebRTC] Wrap-up already open for ${key} — ignoring ${reason}`);
      return false;
    }

    // Bounded: a long shift would otherwise accumulate one entry per call.
    if (wrapUpStartedForRef.current.size > 200) {
      wrapUpStartedForRef.current.clear();
    }
    wrapUpStartedForRef.current.add(key);

    console.log(`[WebRTC] Opening wrap-up for ${key} via ${reason}`);
    enterAwaitingDisposition();
    return true;
  }, [enterAwaitingDisposition]);

  const rememberLeadForCall = useCallback((callId: string | undefined | null, info: CallLeadInfo | null) => {
    if (!callId || !info) return;
    if (info.leadId || info.callAttemptId) {
      leadInfoByCallIdRef.current.set(callId, info);
    }
  }, []);

  const enqueuePendingDisposition = useCallback((info: CallLeadInfo) => {
    if (!(info.leadId || info.callAttemptId)) return;
    const queued = pendingDispositionQueueRef.current;
    const dup = queued.some((p) =>
      (p.callAttemptId && info.callAttemptId && p.callAttemptId === info.callAttemptId)
      || (p.leadId && info.leadId && p.leadId === info.leadId)
    );
    if (dup) return;
    pendingDispositionQueueRef.current = [...queued, info];
    setPendingDispositionCount(pendingDispositionQueueRef.current.length);
    const key = wrapUpKey(info);
    if (key) wrapUpStartedForRef.current.add(key);
  }, []);

  /** Tear down the parked leg. Safe to call when there isn't one. */
  const dropParkLeg = useCallback((reason: string) => {
    const call = parkCallRef.current;
    parkCallRef.current = null;
    parkCallIdRef.current = null;
    if (!call) return;
    console.log(`[WebRTC] Dropping parked leg — ${reason}`);
    try {
      call.hangup?.();
    } catch {
      /* the leg may already be gone */
    }
  }, []);

  /**
   * Establish a parked leg, when the server asks for one.
   *
   * The browser never decides this on its own — `shouldPark` carries the
   * rollout flag, and the destination comes with it, so there is no second
   * copy of either to drift.
   */
  const ensureParked = useCallback((park: { destinationNumber: string; callerNumber: string; agentId: string }) => {
    if (parkCallRef.current || parkingInFlightRef.current) return;
    const client = clientRef.current;
    if (!client || statusRef.current === 'loading' || statusRef.current === 'connecting') return;
    if (!mayPlaceParkLeg({ status: statusRef.current, activeCall: activeCallRef.current })) return;

    parkingInFlightRef.current = true;
    try {
      const clientState = btoa(JSON.stringify({
        agentParkLeg: true,
        agentId: park.agentId,
      }));
      // Our own id, recorded before the call exists. The SDK dispatches a new
      // call's first update synchronously inside newCall(), so an id read off
      // the returned call is assigned too late: that update missed the park
      // branch in the notification handler and fell into ordinary call
      // handling, which claimed the park leg as the agent's call.
      const parkId = crypto.randomUUID();
      parkCallIdRef.current = parkId;
      const call = client.newCall({
        id: parkId,
        destinationNumber: park.destinationNumber,
        callerNumber: park.callerNumber,
        clientState,
      });
      parkCallRef.current = call;
      console.log('[WebRTC] Parked leg requested', { callId: parkId });
    } catch (err) {
      console.error('[WebRTC] Failed to establish parked leg', err);
      parkCallRef.current = null;
      parkCallIdRef.current = null;
    } finally {
      // Released on the next tick so a burst of heartbeats cannot stack parks,
      // while a genuine failure still retries on the following one.
      setTimeout(() => { parkingInFlightRef.current = false; }, 5000);
    }
  }, []);

  /** Act on what the heartbeat came back with. */
  const applyParkDirective = useCallback((result: {
    shouldPark: boolean;
    dropLeg?: boolean;
    park?: { destinationNumber: string; callerNumber: string; agentId: string };
  }) => {
    // A live Instant Connect call flips status to in-call, which immediately
    // heartbeats. If we honoured dropLeg then we would hang up the park leg
    // (and the customer in the conference) on a call-id mismatch.
    if (result.dropLeg && statusRef.current !== 'in-call') {
      dropParkLeg('server does not recognise this leg');
    } else if (result.dropLeg) {
      console.warn('[WebRTC] Ignoring dropLeg while in-call');
    }
    if (result.shouldPark && result.park) ensureParked(result.park);
  }, [dropParkLeg, ensureParked]);

  /**
   * Refuse a transfer, and say why where someone can read it.
   *
   * Every refusal reaches the merchant as SIP 486 and their call ends, so this
   * is the most expensive decision the client makes — and until now the only
   * record of it was a console line in one agent's tab. Measured 2026-09-09:
   * 86 refusals in two hours, and on every one the agent had no live call at
   * all, so each was wrong. Which of the guards fired is the whole question and
   * nothing on the server could see it.
   *
   * Reports the inputs to that decision rather than a verdict, so the answer
   * does not depend on my reading of the guard. Fire and forget: a refusal must
   * not wait on a diagnostic, and a diagnostic that fails must cost nothing.
   */
  const rejectIncomingCall = useCallback((
    call: { hangup?: () => void; id?: string; state?: string | null } | null | undefined,
    reason: string
  ) => {
    console.warn(`[WebRTC] Rejecting incoming call — ${reason}`);
    const existing = activeCallRef.current as { id?: string; state?: string | null } | null;
    void agentService.reportClientReject({
      reason,
      incomingCallId: call?.id ?? null,
      incomingCallState: call?.state ?? null,
      existingCallId: existing?.id ?? null,
      existingCallState: existing?.state ?? null,
      existingReadsAsUp: isCallStillUp(existing),
      parkCallId: parkLegReportId(parkCallRef.current) ?? parkCallIdRef.current,
      // Whether that leg was actually up, which is what the guard now asks.
      // A refusal carrying a park id with `parkLegUp: false` is the bug this
      // was written to find, and after the fix it must not appear again.
      parkLegUp: parkOccupyingCallId(parkCallRef.current) !== null,
      status: statusRef.current ?? null,
      awaitingDisposition: awaitingDispositionRef.current === true,
    }).catch(() => undefined);
    try {
      call?.hangup?.();
    } catch {
      /* non-fatal */
    }
  }, []);

  const trackOutboundCallId = useCallback((call: { id?: string } | null | undefined) => {
    if (call?.id) {
      outboundCallIdsRef.current.add(call.id);
    }
  }, []);

  const clearOutboundCallId = useCallback((call: { id?: string } | null | undefined) => {
    if (call?.id) {
      outboundCallIdsRef.current.delete(call.id);
    }
  }, []);

  const isAgentOutboundCall = useCallback((call: any) => {
    if (call?.direction === 'inbound') return false;
    if (call?.direction === 'outbound') return true;
    if (call?.id && outboundCallIdsRef.current.has(call.id)) return true;
    return isOutboundRef.current;
  }, []);

  // Shared with `reconnect` so a manual retry rebuilds the client with every
  // handler attached, not just ready/error.
  const initWebRTCRef = useRef<(() => Promise<void>) | null>(null);

  // ─── Initialize Telnyx WebRTC ─────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const initWebRTC = async () => {
      try {
        setStatus('loading');
        setError(null);

        // Pre-request microphone permission
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((track) => track.stop());
        } catch (micError) {
          console.warn('Microphone permission denied or not available:', micError);
        }

        // Get WebRTC token
        const { token } = await agentService.getVoiceToken();

        // Dynamically import @telnyx/webrtc
        let TelnyxRTC: any;
        try {
          const mod = await import('@telnyx/webrtc');
          TelnyxRTC = mod.TelnyxRTC || mod.default;
        } catch {
          if (!mounted) return;
          setStatus('error');
          setError('Telnyx WebRTC SDK not installed. Run: npm install @telnyx/webrtc');
          return;
        }

        if (!mounted) return;
        setStatus('connecting');

        // Create a hidden <audio> element for remote audio
        if (!remoteAudioRef.current) {
          // Reuse existing audio element if present to avoid DOM detach/attach races
          // with React strict-mode and SDK lifecycle.
          const existing = document.getElementById('telnyx-remote-audio') as HTMLAudioElement | null;
          if (existing) {
            remoteAudioRef.current = existing;
          } else {
            const audioEl = document.createElement('audio');
            audioEl.id = 'telnyx-remote-audio';
            audioEl.autoplay = true;
            // @ts-ignore
            audioEl.playsInline = true;
            audioEl.volume = 1.0;
            // Prevent the element from being garbage collected
            audioEl.setAttribute('data-managed', 'webrtc');
            document.body.appendChild(audioEl);
            remoteAudioRef.current = audioEl;
          }
        }

        const client = new TelnyxRTC({
          login_token: token,
          ...(process.env.NODE_ENV === 'development' ? { debug: true } : {}),
        });
        client.remoteElement = remoteAudioRef.current;

        // ─── Client Events ─────────────────────────────────────────────
        client.on('telnyx.ready', () => {
          if (!mounted) return;
          setStatus('ready');
          setError(null);
          // Reset outbound flag on every reconnect — prevents a stuck `true`
          // from a previous session from blocking auto-answer on incoming transfers.
          isOutboundRef.current = false;
          // If a socket drop forced the agent unavailable, restore availability
          // now that we're back — otherwise the agent silently stops receiving
          // routed calls until they manually toggle.
          if (availabilityDroppedBySocketRef.current) {
            availabilityDroppedBySocketRef.current = false;
            agentService.setAvailability(true).catch(() => {
              // Leave the flag cleared; agent can re-toggle from the sidebar.
            });
          }
        });

        client.on('telnyx.error', (err: any) => {
          if (!mounted) return;
          const msg = err?.message || err?.error?.message || 'WebRTC connection error';
          setError(msg);
          setStatus('error');
        });

        client.on('telnyx.socket.close', () => {
          if (!mounted) return;
          setStatus('error');
          setError('WebRTC connection lost. Reconnecting...');
          // Mark agent unavailable immediately so the dialer stops routing calls
          // to this SIP endpoint while it's disconnected. Remember whether the
          // agent WAS available so telnyx.ready can restore it after reconnect
          // without overriding a manual "unavailable" choice.
          agentService.getAvailability()
            .then((wasAvailable) => {
              if (!wasAvailable) return;
              availabilityDroppedBySocketRef.current = true;
              return agentService.setAvailability(false);
            })
            .catch(() => {});
          setTimeout(() => {
            if (mounted && clientRef.current) {
              clientRef.current.connect();
            }
          }, 3000);
        });

        client.on('telnyx.notification', (notification: any) => {
          if (!mounted) return;
          try {
          console.log('[WebRTC] Telnyx notification received:', notification);
          const call = notification.call;
          const type = notification.type;

          if (type === 'callUpdate' && call) {
            const callState = call.state;
            console.log(`[WebRTC] Call update: state=${callState}, id=${call.id}`);

            // ─── Parked leg ───
            // Must be handled before anything else. It is an outbound call the
            // browser placed, so every branch below would otherwise claim it:
            // `isAgentOutboundCall` would make it the active call and flip the
            // agent to 'ringing', and its eventual hangup would run wrap-up for
            // a customer that never existed. It is not a call — it is the line
            // calls arrive on.
            if (parkCallIdRef.current && call.id === parkCallIdRef.current) {
              if (callState === 'hangup' || callState === 'destroy') {
                console.log('[WebRTC] Parked leg ended');
                parkCallRef.current = null;
                parkCallIdRef.current = null;
              } else if (callState === 'active') {
                console.log('[WebRTC] Parked leg is up');
                // Attach now, while there is an event to hang it off. The
                // customer arrives by being unheld on the server, which the
                // browser never hears about — there is no second chance.
                const attachParkAudio = (retries = 0) => {
                  const audioEl = remoteAudioRef.current;
                  const stream = call.remoteStream;
                  if (audioEl && stream && stream.getAudioTracks().length > 0) {
                    stream.getAudioTracks().forEach((t: MediaStreamTrack) => { t.enabled = true; });
                    audioEl.srcObject = stream;
                    audioEl.volume = 1.0;
                    audioEl.muted = false;
                    audioEl.play().catch(() => {});
                  } else if (retries < 25) {
                    setTimeout(() => attachParkAudio(retries + 1), 200);
                  }
                };
                attachParkAudio();
              }
              return;
            }

            if (['ringing', 'requesting', 'new', 'invite', 'trying'].includes(callState)) {
              // ─── OUTBOUND CALL: agent initiated via makeOutboundCall ───
              if (isAgentOutboundCall(call)) {
                console.log('[WebRTC] Outbound call ringing (waiting for merchant to answer)...');
                activeCallRef.current = call;
                setStatus('ringing');

                // Attach remote audio early so ringing tone is heard
                const ensureRingingAudio = (retries = 0) => {
                  const audio = remoteAudioRef.current;
                  const stream = call.remoteStream;
                  if (audio && stream && stream.getAudioTracks().length > 0) {
                    stream.getAudioTracks().forEach((t: MediaStreamTrack) => { t.enabled = true; });
                    audio.srcObject = stream;
                    audio.volume = 1.0;
                    audio.muted = false;
                    audio.play().catch(() => {});
                  } else if (retries < 50) {
                    setTimeout(() => ensureRingingAudio(retries + 1), 200);
                  }
                };
                ensureRingingAudio();
                return; // Skip all the incoming-call logic below
              }

              // Inbound auto-dial: wait until SDP is ready — answering on `new`/`requesting`
              // races ICE setup and causes "sdp is null" / "addTrack" crashes in Telnyx SDK.
              if (callState === 'new' || callState === 'requesting') {
                if (
                  !wrapUpMayClaimBrowser({
                    awaitingDisposition: awaitingDispositionRef.current,
                    activeCall: activeCallRef.current,
                    status: statusRef.current,
                  })
                  && shouldRejectIncomingCall({
                    existingCall: activeCallRef.current,
                    incomingCallId: call.id,
                    occupyingCallIds: [parkOccupyingCallId(parkCallRef.current)],
                  })
                ) {
                  rejectIncomingCall(call, 'agent already on a call');
                  return;
                }
                // Reserve the seat before SDP is ready so a second invite cannot sneak in.
                activeCallRef.current = call;
                console.log('[WebRTC] Inbound call setup — waiting for ringing before auto-answer', {
                  callState,
                  callId: call.id,
                });
                return;
              }

              if (answeredCallIdsRef.current.has(call.id)) {
                return;
              }

              // Occupy the first invite. A second transfer used to hang up the
              // first while it was still ringing/trying, which dropped the live
              // merchant and made the next connect feel slow (re-attach + hold).
              //
              // Wrap-up is excluded only when the browser is genuinely free —
              // there `activeCallRef` is stale and the live-connect lookup
              // below is the real gate. It is NOT excluded while a call is up:
              // a disposition is owed all through the next call, so exempting
              // on the flag alone let an arriving transfer overwrite the call
              // the agent was speaking on. See `wrapUpMayClaimBrowser`.
              if (
                !wrapUpMayClaimBrowser({
                  awaitingDisposition: awaitingDispositionRef.current,
                  activeCall: activeCallRef.current,
                  status: statusRef.current,
                })
                && shouldRejectIncomingCall({
                  existingCall: activeCallRef.current,
                  incomingCallId: call.id,
                  occupyingCallIds: [parkOccupyingCallId(parkCallRef.current)],
                })
              ) {
                rejectIncomingCall(call, 'agent already on a call');
                return;
              }

              if (awaitingDispositionRef.current) {
                if (liveConnectCheckInFlightRef.current.has(call.id)) {
                  return;
                }
                liveConnectCheckInFlightRef.current.add(call.id);
                // Claim the seat before the routing lookup so a second invite
                // cannot also auto-answer during wrap-up.
                activeCallRef.current = call;
                void agentService.getMyCurrentLead()
                  .catch(() => null)
                  .then((routing) => {
                    liveConnectCheckInFlightRef.current.delete(call.id);
                    if (!routing?.liveConnect) {
                      if (activeCallRef.current?.id === call.id) activeCallRef.current = null;
                      rejectIncomingCall(call, 'awaiting disposition');
                      return;
                    }
                    const prev = dispositionLeadInfoRef.current ?? callLeadInfoRef.current;
                    if (prev && (prev.leadId || prev.callAttemptId)) {
                      enqueuePendingDisposition(prev);
                    }
                    // Keep setDispositionPending(true) — do not release wrap-up lock.
                    dispositionLeadInfoRef.current = null;
                    awaitingDispositionRef.current = false;
                    setAwaitingDisposition(false);
                    setDispositionSubmitted(null);
                    dispositionSubmittedRef.current = null;
                    setCallLeadInfo(null);
                    callLeadInfoRef.current = null;
                    toast('Live call connected — previous disposition is queued');

                    if (answeredCallIdsRef.current.has(call.id)) return;
                    try {
                      answeredCallIdsRef.current.add(call.id);
                      activeCallRef.current = call;
                      statusRef.current = 'in-call';
                      call.answer();
                    } catch (answerErr) {
                      answeredCallIdsRef.current.delete(call.id);
                      if (activeCallRef.current?.id === call.id) activeCallRef.current = null;
                      // Hand the status back too, exactly as the non-wrap-up
                      // answer below does. Leaving it on `in-call` after an
                      // answer that never happened is how the browser ended up
                      // refusing every later transfer: the status is what the
                      // busy check read, and nothing else ever set it back.
                      // The React state is the half the heartbeat reports, so
                      // it has to move as well or the dialer keeps sending
                      // calls to a client that will not take them.
                      statusRef.current = 'ready';
                      setStatus('ready');
                      console.error('[WebRTC] live-connect answer during wrap-up failed', answerErr);
                      return;
                    }
                    setStatus('in-call');
                    setShowPopup(true);
                    if (routing.leadId || routing.leadName) {
                      setCallLeadInfo(routing);
                      callLeadInfoRef.current = routing;
                      rememberLeadForCall(call.id, routing);
                    }
                  });
                return;
              }

              // ─── INCOMING CALL: server-initiated (auto-dialer transfer) ───
              // Occupy immediately so a second invite in this burst cannot steal
              // the seat while we look up the lead / wait for answer().
              activeCallRef.current = call;
              statusRef.current = 'in-call';

              console.log('[WebRTC] Incoming call detected!');
              const rawCallerNumber =
                call.options?.remoteCallerNumber ||
                call.options?.callerNumber ||
                'Unknown';

              // Normalize: strip SIP URI wrappers and non-digit chars
              let callerNumber = rawCallerNumber;
              if (callerNumber.includes('sip:')) {
                callerNumber = callerNumber.replace(/^sip:/i, '').replace(/@.*$/, '');
              }
              callerNumber = callerNumber.replace(/[^\d+]/g, '');
              if (!callerNumber) callerNumber = rawCallerNumber;

              console.log('[WebRTC] Caller number raw:', rawCallerNumber, 'normalized:', callerNumber);

              // Pre-fetch lead info while ringing
              const callIdForFetch = call.id || callerNumber;
              if (ringingLeadFetchedForRef.current !== callIdForFetch) {
                ringingLeadFetchedForRef.current = callIdForFetch;
                setRingingLeadInfo(null);

                const tryRoutingLookup = agentService.getMyCurrentLead().catch((err) => {
                  console.warn('[WebRTC] Routing-based lead lookup failed:', err?.message || err);
                  return null;
                });
                const tryPhoneLookup =
                  callerNumber && callerNumber !== 'Unknown'
                    ? agentService.getLeadByPhone(callerNumber).catch((err) => {
                      console.warn('[WebRTC] Phone lookup failed:', err?.message || err);
                      return null;
                    })
                    : Promise.resolve(null);

                Promise.all([tryRoutingLookup, tryPhoneLookup]).then(([routingInfo, phoneInfo]) => {
                  console.log('[WebRTC] Lead lookup results - routing:', routingInfo, 'phone:', phoneInfo);
                  const info =
                    (routingInfo?.leadName ? routingInfo : null) ||
                    (phoneInfo?.leadName ? phoneInfo : null) ||
                    routingInfo ||
                    phoneInfo;
                  if (info && (info.leadId || info.leadName)) {
                    setRingingLeadInfo(info);
                    ringingLeadInfoRef.current = info;
                  }
                });
              }

              // Auto-answer immediately (incoming calls only)
              console.log('[WebRTC] Auto-answering incoming call...');
              try {
                answeredCallIdsRef.current.add(call.id);
                call.answer();
              } catch (answerErr) {
                answeredCallIdsRef.current.delete(call.id);
                console.error('[WebRTC] call.answer() failed — call will not connect', answerErr);
                if (activeCallRef.current?.id === call.id) activeCallRef.current = null;
                statusRef.current = 'ready';
                setStatus('ready');
                return;
              }

              // Local-only beep (Web Audio → speakers). Do NOT call muteAudio() here —
              // muting before the peer connection is ready causes "addTrack" null crashes.
              try {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => { });
                const oscillator = audioCtx.createOscillator();
                const gainNode = audioCtx.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioCtx.destination);
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
                gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
                oscillator.start(audioCtx.currentTime);
                oscillator.stop(audioCtx.currentTime + 0.3);
                const osc2 = audioCtx.createOscillator();
                const gain2 = audioCtx.createGain();
                osc2.connect(gain2);
                gain2.connect(audioCtx.destination);
                osc2.type = 'sine';
                osc2.frequency.setValueAtTime(880, audioCtx.currentTime + 0.4);
                gain2.gain.setValueAtTime(0.15, audioCtx.currentTime + 0.4);
                gain2.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.7);
                osc2.start(audioCtx.currentTime + 0.4);
                osc2.stop(audioCtx.currentTime + 0.7);
                setTimeout(() => {
                  audioCtx.close().catch(() => { });
                }, 900);
              } catch (beepErr) {
                console.warn('[WebRTC] Failed to play beep alert:', beepErr);
              }

              // Belt-and-suspenders: manually attach remote audio stream
              // Wait for stream with retry — also listens for track events
              const ensureRemoteAudio = (retries = 0) => {
                const audio = remoteAudioRef.current;
                const stream = call.remoteStream;
                if (audio && stream && stream.getAudioTracks().length > 0) {
                  console.log('[WebRTC] Manually attaching remote stream', {
                    tracks: stream.getAudioTracks().length,
                    active: stream.active,
                    trackEnabled: stream.getAudioTracks().map((t: MediaStreamTrack) => t.enabled),
                  });
                  // Ensure tracks are enabled
                  stream.getAudioTracks().forEach((t: MediaStreamTrack) => { t.enabled = true; });
                  audio.srcObject = stream;
                  audio.volume = 1.0;
                  audio.muted = false;
                  const playPromise = audio.play();
                  if (playPromise) {
                    playPromise.catch((e: any) => {
                      console.warn('[WebRTC] Audio play() failed, retrying with user interaction workaround:', e);
                      // Fallback: retry play on next user interaction
                      const resumePlay = () => {
                        audio.play().catch(() => { });
                        document.removeEventListener('click', resumePlay);
                        document.removeEventListener('keydown', resumePlay);
                      };
                      document.addEventListener('click', resumePlay, { once: true });
                      document.addEventListener('keydown', resumePlay, { once: true });
                    });
                  }
                } else if (retries < 75) {
                  // Retry for up to 15 seconds (75 * 200ms)
                  if (retries % 10 === 0) {
                    console.log(`[WebRTC] Waiting for remote audio stream... (attempt ${retries})`, {
                      streamExists: !!call.remoteStream,
                      trackCount: call.remoteStream?.getAudioTracks()?.length,
                      streamActive: call.remoteStream?.active,
                    });
                  }
                  setTimeout(() => ensureRemoteAudio(retries + 1), 200);
                } else {
                  console.error('[WebRTC] Remote stream not available after 15s retries', {
                    streamExists: !!call.remoteStream,
                    trackCount: call.remoteStream?.getAudioTracks()?.length,
                  });
                }
              };
              // Start checking immediately — no artificial delay
              ensureRemoteAudio();

              statusRef.current = 'in-call';
              setStatus('in-call');
              setDispositionSubmitted(null);

              // Wait for lead/script (same payload as prepareDial / my-current-lead) before showing
              // InCallPopup so auto-dial matches manual dial first paint.
              void (async () => {
                try {
                const pickInboundLead = (routingInfo: CallLeadInfo | null, phoneInfo: CallLeadInfo | null) =>
                  (routingInfo?.leadName ? routingInfo : null) ||
                  (phoneInfo?.leadName ? phoneInfo : null) ||
                  routingInfo ||
                  phoneInfo;

                const routingP = agentService.getMyCurrentLead().catch(() => null);
                const phoneP =
                  callerNumber && callerNumber !== 'Unknown'
                    ? agentService.getLeadByPhone(callerNumber).catch(() => null)
                    : Promise.resolve<CallLeadInfo | null>(null);

                const raced = await Promise.race([
                  Promise.all([routingP, phoneP]).then(([r, p]) => ({ ok: true as const, r, p })),
                  new Promise<{ ok: false }>((res) => setTimeout(() => res({ ok: false }), 2000)),
                ]);

                const [routingInfo, phoneInfo] = raced.ok
                  ? [raced.r, raced.p]
                  : await Promise.all([routingP, phoneP]);

                let info = pickInboundLead(routingInfo, phoneInfo);
                const fromRing = ringingLeadInfoRef.current;
                if (!info || (!info.leadId && !info.leadName)) {
                  info = fromRing;
                }

                const baseLeadInfo: CallLeadInfo = {
                  leadId: info?.leadId ?? null,
                  leadName: info?.leadName ?? null,
                  leadPhone: info?.leadPhone || callerNumber || null,
                  leadEmail: info?.leadEmail ?? null,
                  companyName: info?.companyName ?? null,
                  leadNotes: info?.leadNotes ?? null,
                  campaignName: info?.campaignName ?? null,
                  campaignId: info?.campaignId ?? null,
                  callAttemptId: info?.callAttemptId ?? null,
                  scriptName: info?.scriptName ?? null,
                  scriptContent: info?.scriptContent ?? null,
                  scriptStructure: info?.scriptStructure ?? null,
                };
                if (!awaitingDispositionRef.current) {
                  setCallLeadInfo(baseLeadInfo);
                  callLeadInfoRef.current = baseLeadInfo;
                  rememberLeadForCall(call.id, baseLeadInfo);
                }
                setRingingLeadInfo(null);
                ringingLeadInfoRef.current = null;
                ringingLeadFetchedForRef.current = null;
                setShowPopup(true);
                } catch (e) {
                  console.error('[WebRTC] Incoming lead lookup failed — opening popup with partial info', e);
                  const fr = ringingLeadInfoRef.current;
                  const fallback: CallLeadInfo = {
                    leadId: fr?.leadId ?? null,
                    leadName: fr?.leadName ?? null,
                    leadPhone: fr?.leadPhone || callerNumber || null,
                    leadEmail: fr?.leadEmail ?? null,
                    companyName: fr?.companyName ?? null,
                    leadNotes: fr?.leadNotes ?? null,
                    campaignName: fr?.campaignName ?? null,
                    campaignId: fr?.campaignId ?? null,
                    callAttemptId: fr?.callAttemptId ?? null,
                    scriptName: fr?.scriptName ?? null,
                    scriptContent: fr?.scriptContent ?? null,
                    scriptStructure: fr?.scriptStructure ?? null,
                  };
                  if (!awaitingDispositionRef.current) {
                    setCallLeadInfo(fallback);
                    callLeadInfoRef.current = fallback;
                    rememberLeadForCall(call.id, fallback);
                  }
                  setRingingLeadInfo(null);
                  ringingLeadInfoRef.current = null;
                  ringingLeadFetchedForRef.current = null;
                  setShowPopup(true);
                }
              })();
            } else if (callState === 'active') {
              activeCallRef.current = call;
              if (outboundRingTimeoutRef.current) {
                clearTimeout(outboundRingTimeoutRef.current);
                outboundRingTimeoutRef.current = null;
              }

              // Only attach remote audio if not already attached during 'ringing' state.
              // Re-attaching the same stream causes audio glitches.
              const audio = remoteAudioRef.current;
              const alreadyAttached = audio?.srcObject === call.remoteStream && call.remoteStream;
              if (!alreadyAttached) {
                const attachActiveAudio = (retries = 0) => {
                  const audioEl = remoteAudioRef.current;
                  const stream = call.remoteStream;
                  if (audioEl && stream && stream.getAudioTracks().length > 0) {
                    console.log('[WebRTC] Call active — attaching remote stream', {
                      tracks: stream.getAudioTracks().length,
                      active: stream.active,
                    });
                    stream.getAudioTracks().forEach((t: MediaStreamTrack) => { t.enabled = true; });
                    audioEl.srcObject = stream;
                    audioEl.volume = 1.0;
                    audioEl.muted = false;
                    audioEl.play().catch((e: any) => {
                      console.warn('[WebRTC] Audio play() failed on active state:', e);
                    });
                  } else if (retries < 25) {
                    setTimeout(() => attachActiveAudio(retries + 1), 200);
                  }
                };
                attachActiveAudio();
              } else {
                console.log('[WebRTC] Call active — remote audio already attached, skipping re-attach');
              }

              // Treat `active` as source of truth for both inbound and outbound calls.
              // In production, bridge-alert events can occasionally be missed/delayed,
              // which left agents connected for minutes without seeing dispositions.
              // Using active-state directly prevents that failure mode.
              setStatus('in-call');
              // Don't show the disposition popup for admin test calls
              if (!isTestCallRef.current) {
                if (!callLeadInfoRef.current?.leadId && !callLeadInfoRef.current?.leadName) {
                  void agentService.getMyCurrentLead()
                    .then((info) => {
                      if (awaitingDispositionRef.current) return;
                      if (info && (info.leadId || info.leadName)) {
                        setCallLeadInfo(info);
                        callLeadInfoRef.current = info;
                        rememberLeadForCall(call.id, info);
                      }
                    })
                    .catch(() => undefined);
                }
                if (!awaitingDispositionRef.current) {
                  setShowPopup(true);
                }
              }
            } else if (callState === 'hangup' || callState === 'destroy') {
              clearOutboundCallId(call);
              answeredCallIdsRef.current.delete(call.id);

              const isCurrentCall =
                !activeCallRef.current || activeCallRef.current.id === call.id;
              const endedLead = leadInfoByCallIdRef.current.get(call.id) || null;
              leadInfoByCallIdRef.current.delete(call.id);

              // File what we could hear, before the branch below returns for a
              // superseded call — those are ended calls too, and an agent who
              // heard nothing on one is exactly the report this measures.
              //
              // Only for the call the memo actually belongs to: the monitor
              // restarts its window on a new call id, and filing a stale memo
              // would blame this call for the previous one's silence. Nothing
              // awaits it and a failure is dropped — it is evidence about a
              // call that has already ended.
              const endedAudio = audioMemoRef.current;
              if (endedAudio && endedAudio.callId === call.id) {
                audioMemoRef.current = null;
                const verdict = summariseCallAudio(endedAudio.memo, Date.now());
                const audioCallAttemptId = endedLead?.callAttemptId;
                if (verdict && audioCallAttemptId) {
                  void agentService
                    .reportCallAudio(audioCallAttemptId, verdict)
                    .catch(() => undefined);
                }
              }
              if (!isCurrentCall) {
                if (shouldQueueSupersededWrapUp({
                  endedCallId: call.id,
                  activeCallId: activeCallRef.current?.id,
                  endedLead,
                  alreadyStartedKeys: wrapUpStartedForRef.current,
                }) && endedLead) {
                  enqueuePendingDisposition(endedLead);
                  console.log('[WebRTC] Queued wrap-up for superseded call', {
                    endedId: call.id,
                    activeId: activeCallRef.current?.id,
                    key: wrapUpKey(endedLead),
                  });
                } else {
                  console.log('[WebRTC] Ignoring destroy/hangup for superseded call', {
                    endedId: call.id,
                    activeId: activeCallRef.current?.id,
                  });
                }
                return;
              }

              const wasOutbound = isOutboundRef.current;
              activeCallRef.current = null;
              // Synchronous, like the answer-failure path above. `setStatus` only
              // reaches statusRef on the next effect flush, and the second-invite
              // guard reads statusRef as `occupied` — so between the two the next
              // legitimate transfer was rejected with no active call to protect.
              statusRef.current = 'ready';
              isOutboundRef.current = false; // Reset outbound flag
              const wasTestCall = isTestCallRef.current;
              if (wasTestCall) {
                isTestCallRef.current = false;
                setIsTestCall(false);
              }
              if (outboundRingTimeoutRef.current) {
                clearTimeout(outboundRingTimeoutRef.current);
                outboundRingTimeoutRef.current = null;
              }
              setStatus('ready');
              setIsMuted(false);

              releaseRemoteAudio(remoteAudioRef.current, parkCallRef.current);
              if (wasTestCall) {
                // For test calls, just clean up without requiring a disposition
                setShowPopup(false);
                setAwaitingDisposition(false);
                setCallLeadInfo(null);
                setDispositionSubmitted(null);
              } else if (dispositionSubmittedRef.current) {
                // For regular calls, close popup only if disposition was already submitted
                // and no parked wrap-up remains (live-connect accept queues the previous one).
                if (pendingDispositionQueueRef.current.length === 0) {
                  setShowPopup(false);
                  setAwaitingDisposition(false);
                  setCallLeadInfo(null);
                  releaseDispositionContext();
                }
              } else if (wasOutbound && callLeadInfoRef.current) {
                // Outbound ended without disposition — show disposition UI even if WebRTC
                // never reached in-call (common when merchant leg rings but bridge is delayed).
                const info = callLeadInfoRef.current;
                enterAwaitingDisposition();
                if (info.leadId || info.callAttemptId) {
                  agentService
                    .cancelDial({
                      leadId: info.leadId || undefined,
                      callAttemptId: info.callAttemptId || undefined,
                    })
                    .catch(() => undefined);
                }
              } else if (callLeadInfoRef.current || dispositionLeadInfoRef.current) {
                // Inbound auto-dial ended without disposition — lock context before next dial.
                // Shared with the server's call-ended alert; first one through wins.
                beginWrapUp('webrtc-hangup');
              }
            }
          }
          } catch (notifErr) {
            console.error('[WebRTC] Unhandled error in telnyx.notification handler', notifErr);
          }
        });

        await client.connect();
        clientRef.current = client;
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to initialize WebRTC');
        setStatus('error');
      }
    };

    initWebRTCRef.current = initWebRTC;
    initWebRTC();

    return () => {
      mounted = false;
      initWebRTCRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      if (outboundRingTimeoutRef.current) {
        clearTimeout(outboundRingTimeoutRef.current);
        outboundRingTimeoutRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      if (remoteAudioRef.current) {
        // Keep the shared audio node mounted; only clear stream reference.
        // Removing the node during SDK teardown can cause removeChild races.
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current = null;
      }
    };
  }, []);

  // ─── Call Timer ────────────────────────────────────────────────────────
  useEffect(() => {
    if (status === 'in-call') {
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
    return undefined;
  }, [status]);

  const handleCallAlert = useCallback(
    async (result: {
      voicemailDetected: boolean;
      callAttemptId?: string;
      callBridged?: boolean;
      noAnswerDetected?: boolean;
      callEnded?: boolean;
      screeningDetected?: boolean;
    }) => {
      // Apple call screening. The audio is indistinguishable from voicemail, so
      // without this the agent hangs up on someone who is listening live and
      // can still pick up. Long-lived and manually dismissable, because it has
      // to survive the seconds the agent spends deciding.
      const screeningKey = result.callAttemptId || callLeadInfoRef.current?.callAttemptId || null;
      if (result.screeningDetected && screeningKey && screeningNotifiedForRef.current !== screeningKey) {
        screeningNotifiedForRef.current = screeningKey;
        toast(
          'iPhone call screening — they are listening live. Keep talking, they can pick up.',
          { icon: '📱', duration: 15000, id: 'ios-screening' }
        );
      }

      // Server says this call is over. Do NOT hang up the local leg here — the
      // agent's leg is allowed to outlive the call, and under a parked leg it
      // must. This only opens wrap-up.
      if (result.callEnded && !dispositionSubmittedRef.current) {
        beginWrapUp('server-call-ended');
      }

      if (result.voicemailDetected) {
        console.log('[WebRTC] Voicemail detected from call-alert stream');
        setVoicemailDetected(true);
        toast.error('Voicemail detected — you can end the call manually');
      }

      const awaitingOutboundFinalize =
        !dispositionSubmittedRef.current &&
        !!callLeadInfoRef.current?.callAttemptId &&
        statusRef.current !== 'in-call';

      // Auto-dial inbound: backend bridged but WebRTC UI/audio path may have missed the popup.
      const awaitingInboundBridgeUi =
        !dispositionSubmittedRef.current &&
        !isOutboundRef.current &&
        statusRef.current !== 'in-call';

      if (result.callBridged && awaitingInboundBridgeUi) {
        if (awaitingDispositionRef.current) {
          return;
        }
        console.log('[WebRTC] Auto-dial bridge alert — opening in-call UI');
        try {
          const routingInfo = await agentService.getMyCurrentLead().catch(() => null);
          if (!awaitingDispositionRef.current && routingInfo && (routingInfo.leadId || routingInfo.leadName)) {
            setCallLeadInfo(routingInfo);
            callLeadInfoRef.current = routingInfo;
            rememberLeadForCall(activeCallRef.current?.id, routingInfo);
          }
        } catch {
          /* non-fatal */
        }
        if (!awaitingDispositionRef.current) {
          setStatus('in-call');
          setShowPopup(true);
        }
      }

      if (awaitingOutboundFinalize) {
        if (result.callBridged && statusRef.current === 'ringing') {
          // Keep as secondary signal only; active-state handler is now primary.
          console.log('[WebRTC] Merchant answered — bridge confirmed.');
          setStatus('in-call');
          setShowPopup(true);
        }
        if (result.noAnswerDetected) {
          console.log('[WebRTC] Merchant did not answer — opening disposition screen.');
          if (activeCallRef.current) {
            try {
              activeCallRef.current.hangup();
            } catch {
              /* non-fatal */
            }
            activeCallRef.current = null;
          }
          releaseRemoteAudio(remoteAudioRef.current, parkCallRef.current);
          setStatus('ready');
          setIsMuted(false);
          enterAwaitingDisposition();
          toast('Lead did not answer — select a disposition', { icon: '📋' });
        }
      }
    },
    [enterAwaitingDisposition, beginWrapUp]
  );

  // ─── Call Alert Listener (SSE primary, polling fallback) ──────────────
  useEffect(() => {
    const shouldListenForAlerts =
      status === 'ready' ||
      status === 'in-call' ||
      status === 'ringing';
    const scopedCallAttemptId = callLeadInfoRef.current?.callAttemptId || undefined;

    if (!shouldListenForAlerts) {
      setVoicemailDetected(false);
      return undefined;
    }

    let stopped = false;
    let eventSource: EventSource | null = null;
    let fallbackPoll: ReturnType<typeof setInterval> | null = null;

    const startFallbackPolling = (intervalMs: number) => {
      if (fallbackPoll || stopped) return;
      console.warn(`[WebRTC] Using call-alert polling fallback every ${intervalMs}ms`);
      fallbackPoll = setInterval(async () => {
        try {
          const result = await agentService.checkCallAlert(scopedCallAttemptId);
          await handleCallAlert(result);
        } catch {
          // Ignore transient polling errors
        }
      }, intervalMs);
    };

    const pollingIntervalMs = status === 'ringing' ? 3000 : 4000;

    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      startFallbackPolling(pollingIntervalMs);
      return () => {
        stopped = true;
        if (fallbackPoll) clearInterval(fallbackPoll);
      };
    }

    try {
      eventSource = new EventSource(agentService.getCallAlertStreamUrl(scopedCallAttemptId));
      eventSource.addEventListener('call-alert', async (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          await handleCallAlert(data);
        } catch {
          // Ignore malformed event payloads
        }
      });

      eventSource.onerror = () => {
        if (!stopped) {
          eventSource?.close();
          eventSource = null;
          startFallbackPolling(pollingIntervalMs);
        }
      };
    } catch {
      startFallbackPolling(pollingIntervalMs);
    }

    return () => {
      stopped = true;
      if (eventSource) {
        eventSource.close();
      }
      if (fallbackPoll) {
        clearInterval(fallbackPoll);
      }
    };
  }, [status, handleCallAlert]);

  // Keep refs in sync with state
  useEffect(() => { callLeadInfoRef.current = callLeadInfo; }, [callLeadInfo]);
  useEffect(() => { dispositionSubmittedRef.current = dispositionSubmitted; }, [dispositionSubmitted]);

  // ─── Call Actions ─────────────────────────────────────────────────────
  const hangUp = useCallback(async () => {
    // 1. Immediately disconnect local WebRTC leg
    if (activeCallRef.current) {
      clearOutboundCallId(activeCallRef.current);
      activeCallRef.current.hangup();
    }
    activeCallRef.current = null;
    releaseRemoteAudio(remoteAudioRef.current, parkCallRef.current);
    setStatus('ready');
    setIsMuted(false);

    const info = callLeadInfoRef.current;
    const alreadyDispositioned = dispositionSubmittedRef.current;

    // 2. End Telnyx legs (best effort) — disposition is chosen separately in the popup.
    if (info?.leadId || info?.callAttemptId) {
      try {
        await agentService.cancelDial({
          leadId: info?.leadId || undefined,
          callAttemptId: info?.callAttemptId || undefined,
        });
      } catch (err: any) {
        console.warn('[WebRTC] cancelDial failed (call may already be ended):', err?.message || err);
      }
    }

    // 3. Close if disposition already saved and nothing is queued; otherwise keep popup.
    if (alreadyDispositioned) {
      setTimeout(() => {
        if (pendingDispositionQueueRef.current.length > 0) return;
        setShowPopup(false);
        setAwaitingDisposition(false);
        setCallLeadInfo(null);
        setDispositionSubmitted(null);
        releaseDispositionContext();
        onDispositionCompleteRef.current?.({
          leadId: info?.leadId || null,
          disposition: alreadyDispositioned,
        });
      }, 500);
      return;
    }

    enterAwaitingDisposition();
  }, [enterAwaitingDisposition, releaseDispositionContext]);

  useEffect(() => {
    hangUpRef.current = hangUp;
  }, [hangUp]);

  const handleDisposition = useCallback(
    async (
      value: DispositionValue,
      notes?: string,
      followUpDate?: string,
      extras?: {
        subStageId?: string;
        mainStatusChange?: string;
        followUpOnly?: boolean;
        callHistoryOnly?: boolean;
      }
    ) => {
      try {
        const info = dispositionLeadInfoRef.current ?? callLeadInfoRef.current;
        if (!info?.leadId && !info?.callAttemptId) {
          toast.error('No call context for disposition — please try again');
          return;
        }
        const callHistoryOnly =
          extras?.callHistoryOnly || dispositionModeRef.current === 'results_callback';

        await agentService.setDisposition({
          leadId: info?.leadId || null,
          callAttemptId: info?.callAttemptId || null,
          disposition: value,
          notes,
          ...(followUpDate ? { followUpDate } : {}),
          ...(extras?.subStageId ? { subStageId: extras.subStageId } : {}),
          ...(extras?.mainStatusChange ? { mainStatusChange: extras.mainStatusChange } : {}),
          ...(extras?.followUpOnly ? { followUpOnly: true } : {}),
          ...(callHistoryOnly ? { callHistoryOnly: true } : {}),
        });
        setDispositionSubmitted(value);
        dispositionSubmittedRef.current = value;
        toast.success(`Disposition: ${value.replace(/_/g, ' ')}`);

        // If the call is still active, end the local WebRTC leg
        if (activeCallRef.current) {
          activeCallRef.current.hangup();
          activeCallRef.current = null;
          releaseRemoteAudio(remoteAudioRef.current, parkCallRef.current);
          setStatus('ready');
          setIsMuted(false);
        }

        // Disconnect Telnyx legs — disposition is already persisted above.
        if (info?.leadId || info?.callAttemptId) {
          agentService.cancelDial({
            leadId: info?.leadId || undefined,
            callAttemptId: info?.callAttemptId || undefined,
            disposition: value,
          }).catch((err) => {
            console.warn('[WebRTC] cancelDial after disposition failed:', err?.message || err);
          });
        }

        // Close popup, or surface the next parked disposition
        setTimeout(() => {
          onDispositionCompleteRef.current?.({
            leadId: info?.leadId || null,
            disposition: value,
          });
          const queued = pendingDispositionQueueRef.current;
          if (queued.length > 0) {
            const [next, ...rest] = queued;
            pendingDispositionQueueRef.current = rest;
            setPendingDispositionCount(rest.length);
            lockDispositionContext(next);
            setCallLeadInfo(next);
            callLeadInfoRef.current = next;
            setAwaitingDisposition(true);
            awaitingDispositionRef.current = true;
            setShowPopup(true);
            setDispositionSubmitted(null);
            dispositionSubmittedRef.current = null;
            if (rest.length > 0) {
              toast(`${rest.length} pending disposition${rest.length === 1 ? '' : 's'} remaining`);
            }
            return;
          }
          setShowPopup(false);
          setAwaitingDisposition(false);
          setCallLeadInfo(null);
          setDispositionSubmitted(null);
          dispositionSubmittedRef.current = null;
          dispositionModeRef.current = 'full';
          setDispositionMode('full');
          releaseDispositionContext();
        }, 500);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to set disposition');
      }
    },
    [lockDispositionContext, releaseDispositionContext],
  );

  const toggleMute = useCallback(() => {
    if (!activeCallRef.current) return;
    const next = !isMuted;
    if (next) {
      activeCallRef.current.muteAudio();
    } else {
      activeCallRef.current.unmuteAudio();
    }
    setIsMuted(next);
  }, [isMuted]);

  const setOnDispositionComplete = useCallback((cb: ((payload?: { leadId?: string | null; disposition?: DispositionValue | null }) => void) | null) => {
    onDispositionCompleteRef.current = cb;
  }, []);

  // ─── Outbound Call (WebRTC Direct) ────────────────────────────────────
  const makeOutboundCall = useCallback((params: OutboundCallParams) => {
    const client = clientRef.current;
    if (!client) {
      toast.error('WebRTC not connected. Please refresh the page.');
      return;
    }
    if (awaitingDispositionRef.current) {
      toast.error('Please complete the current call disposition before dialing again.');
      return;
    }

    // Build clientState for Telnyx webhooks to track this call
    const clientState = btoa(JSON.stringify({
      callAttemptId: params.callAttemptId,
      campaignId: params.campaignId,
      leadId: params.leadId,
      leadName: params.leadName,
      routingAttemptId: params.routingAttemptId,
      routingLegId: params.routingLegId,
      agentId: params.agentId,
      manualDial: true,
      webrtcDirect: true,
    }));

    // Mark as outbound BEFORE creating the call so the notification handler
    // knows not to auto-answer when it receives the 'ringing' state.
    isOutboundRef.current = true;
    if (outboundRingTimeoutRef.current) {
      clearTimeout(outboundRingTimeoutRef.current);
      outboundRingTimeoutRef.current = null;
    }

    // Initiate the outbound call directly from the browser
    const call = client.newCall({
      destinationNumber: params.destinationNumber,
      callerNumber: params.callerNumber,
      clientState,
    });

    trackOutboundCallId(call);
    activeCallRef.current = call;
    setStatus('ringing');
    setDispositionSubmitted(null);
    dispositionSubmittedRef.current = null;
    setVoicemailDetected(false);
    setAwaitingDisposition(false);

    // Set lead info immediately (we have it from prepare-dial)
    const leadInfo: CallLeadInfo = {
      leadId: params.leadId,
      leadName: params.leadName,
      leadPhone: params.leadPhone || params.destinationNumber,
      leadEmail: params.leadEmail || null,
      companyName: params.companyName || null,
      leadNotes: params.leadNotes || null,
      campaignName: params.campaignName || null,
      campaignId: params.campaignId,
      callAttemptId: params.callAttemptId,
      scriptName: params.scriptName || null,
      scriptContent: params.scriptContent || null,
      scriptStructure: params.scriptStructure || null,
      leadStatus: params.leadStatus || null,
      preDialStatus: params.preDialStatus || null,
      subStageId: params.subStageId || null,
      dispositionMode: params.dispositionMode || 'full',
    };
    const mode = params.dispositionMode || 'full';
    dispositionModeRef.current = mode;
    setDispositionMode(mode);
    setCallLeadInfo(leadInfo);
    callLeadInfoRef.current = leadInfo;
    rememberLeadForCall(call.id, leadInfo);
    // Don't show full popup during ringing — a separate ringing indicator is shown instead.
    // Popup will open when status transitions to 'in-call' (active state handler).

    // Safety guard: if outbound call stays ringing too long without reaching in-call,
    // fail fast instead of leaving agents in prolonged silence/waiting state.
    // Backend Telnyx ring timeout defaults to config.telnyx.ringTimeoutSecs (30s); hang up slightly
    // after so the server—not the browser—owns cutoff under normal conditions.
    const OUTBOUND_MERCHANT_RING_GUARD_MS = 45_000;
    outboundRingTimeoutRef.current = setTimeout(() => {
      const stillOutbound = isOutboundRef.current;
      const stillRinging = statusRef.current === 'ringing';
      const stillSameCall = !!activeCallRef.current;
      if (stillOutbound && stillRinging && stillSameCall) {
        toast.error('No response from merchant yet. Ending call attempt.', { duration: 4000 });
        void hangUpRef.current?.();
      }
    }, OUTBOUND_MERCHANT_RING_GUARD_MS);

    console.log('[WebRTC] Outbound call initiated', {
      to: params.destinationNumber,
      from: params.callerNumber,
      callAttemptId: params.callAttemptId,
    });
  }, []);

  // ─── Admin Test Call (direct WebRTC, no campaign routing) ────────────
  const startTestCall = useCallback((fromNumber: string, toNumber: string) => {
    const client = clientRef.current;
    if (!client) {
      toast.error('WebRTC not ready. Please wait a moment and try again.');
      return;
    }
    if (statusRef.current !== 'ready') {
      toast.error(`WebRTC is ${statusRef.current}. Please wait for it to be ready.`);
      return;
    }

    isOutboundRef.current = true;
    isTestCallRef.current = true;
    setIsTestCall(true);

    const call = client.newCall({
      destinationNumber: toNumber,
      callerNumber: fromNumber,
    });

    trackOutboundCallId(call);
    activeCallRef.current = call;
    setStatus('ringing');
    setDispositionSubmitted(null);
    dispositionSubmittedRef.current = null;
    setVoicemailDetected(false);

    const testLeadInfo: CallLeadInfo = {
      leadId: null,
      leadName: null,
      leadPhone: toNumber,
      leadEmail: null,
      companyName: null,
      leadNotes: null,
      campaignName: null,
      campaignId: null,
      callAttemptId: null,
      scriptName: null,
      scriptContent: null,
      scriptStructure: null,
    };
    setCallLeadInfo(testLeadInfo);
    callLeadInfoRef.current = testLeadInfo;

    console.log('[WebRTC] Admin test call initiated', { from: fromNumber, to: toNumber });
  }, []);

  const hangUpTestCall = useCallback(() => {
    if (activeCallRef.current) {
      try { activeCallRef.current.hangup(); } catch { /* non-fatal */ }
    }
    activeCallRef.current = null;
    isOutboundRef.current = false;
    isTestCallRef.current = false;
    setIsTestCall(false);

    if (outboundRingTimeoutRef.current) {
      clearTimeout(outboundRingTimeoutRef.current);
      outboundRingTimeoutRef.current = null;
    }
    releaseRemoteAudio(remoteAudioRef.current, parkCallRef.current);

    setStatus('ready');
    setIsMuted(false);
    setShowPopup(false);
    setCallLeadInfo(null);
    setDispositionSubmitted(null);
    console.log('[WebRTC] Admin test call ended');
  }, []);

  // Minimal silent WAV — played inside a user gesture to satisfy the browser's
  // autoplay policy so the remote-audio element can play() when a transferred
  // call auto-answers later (otherwise the agent gets one-way/no audio until
  // they happen to click somewhere).
  const SILENT_WAV =
    'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';

  const unlockAudio = useCallback(() => {
    const audio =
      remoteAudioRef.current ||
      (document.getElementById('telnyx-remote-audio') as HTMLAudioElement | null);
    if (!audio || audio.srcObject) return; // live call in progress — leave it alone
    audio.src = SILENT_WAV;
    audio.muted = false;
    const p = audio.play();
    if (p) {
      p.then(() => {
        audio.pause();
        // srcObject assignment during a real call takes precedence over src,
        // so the stale silent src is harmless to leave in place.
      }).catch(() => {
        /* best-effort — worst case we fall back to the click/keydown resume */
      });
    }
  }, []);

  const reconnect = useCallback(async () => {
    const init = initWebRTCRef.current;
    if (!init) return;
    setStatus('loading');
    setError(null);
    if (clientRef.current) {
      try {
        clientRef.current.disconnect();
      } catch {
        // Socket may already be gone; the rebuild below is what matters.
      }
      clientRef.current = null;
    }
    // Rebuild through the same bootstrap the app starts with. Registering only
    // ready/error here left the agent connected but deaf to telnyx.notification,
    // so routed calls never reached the browser.
    await init();
  }, []);

  // ─── Context Value ────────────────────────────────────────────────────
  const value: WebRTCContextType = {
    status,
    error,
    isMuted,
    reconnect,
    hangUp,
    toggleMute,
    handleDisposition,
    callLeadInfo,
    seconds,
    dispositionSubmitted,
    setOnDispositionComplete,
    voicemailDetected,
    remoteAudio,
    makeOutboundCall,
    isTestCall,
    startTestCall,
    hangUpTestCall,
    unlockAudio,
    getParkCallId: () => parkLegReportId(parkCallRef.current),
    applyParkDirective,
  };

  return (
    <WebRTCContext.Provider value={value}>
      {children}

      {/* ─── Ringing Indicator (outbound manual dial, before answer) ─── */}
      {status === 'ringing' && isOutboundRef.current && !showPopup && callLeadInfo && !isTestCall && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[340px] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <PhoneIcon className="h-5 w-5 text-white animate-bounce" />
                </div>
                <div>
                  <span className="text-white text-sm font-semibold">Ringing...</span>
                  <div className="text-blue-100 text-xs">Waiting for answer</div>
                </div>
              </div>
              {/* Pulsing ring indicator */}
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
            </div>

            {/* Lead Info */}
            <div className="px-5 py-4 space-y-2">
              {callLeadInfo.leadName && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="font-semibold text-foreground">{callLeadInfo.leadName}</span>
                </div>
              )}
              {callLeadInfo.companyName && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Merchant:</span>
                  <span className="font-semibold text-foreground">{callLeadInfo.companyName}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono text-foreground">{callLeadInfo.leadPhone || 'Unknown'}</span>
              </div>
            </div>

            {/* Cancel Button */}
            <div className="px-5 py-3 border-t border-border flex justify-center">
              <button
                onClick={hangUp}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-all duration-200 shadow-lg shadow-red-500/20"
              >
                <PhoneXMarkIcon className="h-4 w-4" />
                Cancel Call
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global In-Call Popup — renders on every page (not for admin test calls) */}
      {showPopup && !isTestCall && (
        <InCallPopup
          dispositionMode={dispositionMode}
          isActive={status === 'in-call'}
          awaitingDisposition={awaitingDisposition}
          pendingDispositionCount={pendingDispositionCount}
          leadInfo={callLeadInfo}
          durationSeconds={seconds}
          isMuted={isMuted}
          onHangUp={hangUp}
          onToggleMute={toggleMute}
          onDisposition={handleDisposition}
          dispositionSubmitted={dispositionSubmitted}
          voicemailDetected={voicemailDetected}
          remoteAudio={remoteAudio}
        />
      )}
      {!isTestCall && (
        <ScriptAssistPopup
          isActive={status === 'in-call'}
          leadInfo={callLeadInfo}
          callStatus={status}
        />
      )}
    </WebRTCContext.Provider>
  );
};
